import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useState, useRef } from "react";

export interface ValeTransporteData {
  // IDA
  ida_integracao_onibus_metro: boolean;
  ida_integracao_onibus_metro_valor: number;
  ida_integracao_onibus_trem: boolean;
  ida_integracao_onibus_trem_valor: number;
  ida_onibus_municipal: boolean;
  ida_onibus_municipal_valor: number;
  ida_onibus_intermunicipal: boolean;
  ida_onibus_intermunicipal_valor: number;
  ida_metro_trem: boolean;
  ida_metro_trem_valor: number;
  ida_outros: boolean;
  ida_outros_descricao: string;
  ida_outros_valor: number;
  
  // VOLTA
  volta_integracao_onibus_metro: boolean;
  volta_integracao_onibus_metro_valor: number;
  volta_integracao_onibus_trem: boolean;
  volta_integracao_onibus_trem_valor: number;
  volta_onibus_municipal: boolean;
  volta_onibus_municipal_valor: number;
  volta_onibus_intermunicipal: boolean;
  volta_onibus_intermunicipal_valor: number;
  volta_metro_trem: boolean;
  volta_metro_trem_valor: number;
  volta_outros: boolean;
  volta_outros_descricao: string;
  volta_outros_valor: number;
  
  // Tipo de cartão
  tipo_cartao: string;
  tipo_cartao_outros: string;
}

interface ValeTransporteFormProps {
  data: ValeTransporteData;
  onChange: (data: ValeTransporteData) => void;
}

export default function ValeTransporteForm({ data, onChange }: ValeTransporteFormProps) {
  const handleCheckboxChange = (field: keyof ValeTransporteData, checked: boolean) => {
    onChange({ ...data, [field]: checked });
  };

  const handleValueChange = (field: keyof ValeTransporteData, value: string) => {
    // Remover caracteres não numéricos exceto vírgula e ponto
    const cleanValue = value.replace(/[^\d.,]/g, '');
    // Substituir vírgula por ponto para conversão
    const normalizedValue = cleanValue.replace(',', '.');
    // Converter para número
    const numValue = normalizedValue === '' ? 0 : parseFloat(normalizedValue) || 0;
    onChange({ ...data, [field]: numValue });
  };

  const handleTextChange = (field: keyof ValeTransporteData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const handleTipoCartaoChange = (value: string) => {
    onChange({ ...data, tipo_cartao: value });
  };

  const formatCurrency = (value: number): string => {
    if (value === 0) return '';
    return value.toFixed(2).replace('.', ',');
  };

  // Calcular totais
  const totalIda = 
    (data.ida_integracao_onibus_metro ? data.ida_integracao_onibus_metro_valor : 0) +
    (data.ida_integracao_onibus_trem ? data.ida_integracao_onibus_trem_valor : 0) +
    (data.ida_onibus_municipal ? data.ida_onibus_municipal_valor : 0) +
    (data.ida_onibus_intermunicipal ? data.ida_onibus_intermunicipal_valor : 0) +
    (data.ida_metro_trem ? data.ida_metro_trem_valor : 0) +
    (data.ida_outros ? data.ida_outros_valor : 0);

  const totalVolta = 
    (data.volta_integracao_onibus_metro ? data.volta_integracao_onibus_metro_valor : 0) +
    (data.volta_integracao_onibus_trem ? data.volta_integracao_onibus_trem_valor : 0) +
    (data.volta_onibus_municipal ? data.volta_onibus_municipal_valor : 0) +
    (data.volta_onibus_intermunicipal ? data.volta_onibus_intermunicipal_valor : 0) +
    (data.volta_metro_trem ? data.volta_metro_trem_valor : 0) +
    (data.volta_outros ? data.volta_outros_valor : 0);

  const totalDiario = totalIda + totalVolta;
  const excedeValor = totalDiario > 21;

  const TransportOption = ({ 
    label, 
    checked, 
    value, 
    onCheckChange, 
    onValueChange,
    showDescription = false,
    description = "",
    onDescriptionChange
  }: {
    label: string;
    checked: boolean;
    value: number;
    onCheckChange: (checked: boolean) => void;
    onValueChange: (value: string) => void;
    showDescription?: boolean;
    description?: string;
    onDescriptionChange?: (value: string) => void;
  }) => {
    const [localValue, setLocalValue] = useState(formatCurrency(value));
    const [localDescription, setLocalDescription] = useState(description || '');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      onValueChange(localValue);
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalDescription(e.target.value);
    };

    const handleDescriptionBlur = () => {
      onDescriptionChange?.(localDescription);
    };

    return (
      <div className="flex items-center gap-4 py-2">
        <Checkbox
          checked={checked}
          onCheckedChange={onCheckChange}
        />
        <Label className="flex-1 cursor-pointer" onClick={() => onCheckChange(!checked)}>
          {label}
        </Label>
        {showDescription && (
          <Input
            type="text"
            placeholder="Descrição"
            value={localDescription}
            onChange={handleDescriptionChange}
            onBlur={handleDescriptionBlur}
            onFocus={(e) => e.target.select()}
            className="w-48"
            disabled={!checked}
          />
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">R$</span>
          <Input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={localValue}
            onChange={handleLocalChange}
            onBlur={handleBlur}
            onFocus={(e) => e.target.select()}
            className="w-28 text-right"
            disabled={!checked}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Seção IDA */}
      <Card>
        <CardHeader>
          <CardTitle>Meios de Transporte — IDA</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <TransportOption
            label="Integração Ônibus/Metrô"
            checked={data.ida_integracao_onibus_metro}
            value={data.ida_integracao_onibus_metro_valor}
            onCheckChange={(checked) => handleCheckboxChange('ida_integracao_onibus_metro', checked)}
            onValueChange={(value) => handleValueChange('ida_integracao_onibus_metro_valor', value)}
          />
          <TransportOption
            label="Integração Ônibus/Trem"
            checked={data.ida_integracao_onibus_trem}
            value={data.ida_integracao_onibus_trem_valor}
            onCheckChange={(checked) => handleCheckboxChange('ida_integracao_onibus_trem', checked)}
            onValueChange={(value) => handleValueChange('ida_integracao_onibus_trem_valor', value)}
          />
          <TransportOption
            label="Ônibus Municipal"
            checked={data.ida_onibus_municipal}
            value={data.ida_onibus_municipal_valor}
            onCheckChange={(checked) => handleCheckboxChange('ida_onibus_municipal', checked)}
            onValueChange={(value) => handleValueChange('ida_onibus_municipal_valor', value)}
          />
          <TransportOption
            label="Ônibus Intermunicipal"
            checked={data.ida_onibus_intermunicipal}
            value={data.ida_onibus_intermunicipal_valor}
            onCheckChange={(checked) => handleCheckboxChange('ida_onibus_intermunicipal', checked)}
            onValueChange={(value) => handleValueChange('ida_onibus_intermunicipal_valor', value)}
          />
          <TransportOption
            label="Metrô ou Trem"
            checked={data.ida_metro_trem}
            value={data.ida_metro_trem_valor}
            onCheckChange={(checked) => handleCheckboxChange('ida_metro_trem', checked)}
            onValueChange={(value) => handleValueChange('ida_metro_trem_valor', value)}
          />
          <TransportOption
            label="Outros"
            checked={data.ida_outros}
            value={data.ida_outros_valor}
            onCheckChange={(checked) => handleCheckboxChange('ida_outros', checked)}
            onValueChange={(value) => handleValueChange('ida_outros_valor', value)}
            showDescription
            description={data.ida_outros_descricao}
            onDescriptionChange={(value) => handleTextChange('ida_outros_descricao', value)}
          />
        </CardContent>
      </Card>

      {/* Seção VOLTA */}
      <Card>
        <CardHeader>
          <CardTitle>Meios de Transporte — VOLTA</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <TransportOption
            label="Integração Ônibus/Metrô"
            checked={data.volta_integracao_onibus_metro}
            value={data.volta_integracao_onibus_metro_valor}
            onCheckChange={(checked) => handleCheckboxChange('volta_integracao_onibus_metro', checked)}
            onValueChange={(value) => handleValueChange('volta_integracao_onibus_metro_valor', value)}
          />
          <TransportOption
            label="Integração Ônibus/Trem"
            checked={data.volta_integracao_onibus_trem}
            value={data.volta_integracao_onibus_trem_valor}
            onCheckChange={(checked) => handleCheckboxChange('volta_integracao_onibus_trem', checked)}
            onValueChange={(value) => handleValueChange('volta_integracao_onibus_trem_valor', value)}
          />
          <TransportOption
            label="Ônibus Municipal"
            checked={data.volta_onibus_municipal}
            value={data.volta_onibus_municipal_valor}
            onCheckChange={(checked) => handleCheckboxChange('volta_onibus_municipal', checked)}
            onValueChange={(value) => handleValueChange('volta_onibus_municipal_valor', value)}
          />
          <TransportOption
            label="Ônibus Intermunicipal"
            checked={data.volta_onibus_intermunicipal}
            value={data.volta_onibus_intermunicipal_valor}
            onCheckChange={(checked) => handleCheckboxChange('volta_onibus_intermunicipal', checked)}
            onValueChange={(value) => handleValueChange('volta_onibus_intermunicipal_valor', value)}
          />
          <TransportOption
            label="Metrô ou Trem"
            checked={data.volta_metro_trem}
            value={data.volta_metro_trem_valor}
            onCheckChange={(checked) => handleCheckboxChange('volta_metro_trem', checked)}
            onValueChange={(value) => handleValueChange('volta_metro_trem_valor', value)}
          />
          <TransportOption
            label="Outros"
            checked={data.volta_outros}
            value={data.volta_outros_valor}
            onCheckChange={(checked) => handleCheckboxChange('volta_outros', checked)}
            onValueChange={(value) => handleValueChange('volta_outros_valor', value)}
            showDescription
            description={data.volta_outros_descricao}
            onDescriptionChange={(value) => handleTextChange('volta_outros_descricao', value)}
          />
        </CardContent>
      </Card>

      {/* Tipo de Cartão */}
      <Card>
        <CardHeader>
          <CardTitle>Tipo de Cartão</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value={data.tipo_cartao} onValueChange={handleTipoCartaoChange}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="bilhete_unico" id="bilhete_unico" />
              <Label htmlFor="bilhete_unico" className="cursor-pointer">Bilhete Único</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="cartao_bom" id="cartao_bom" />
              <Label htmlFor="cartao_bom" className="cursor-pointer">Cartão Top</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="cartao_dia_facil" id="cartao_dia_facil" />
              <Label htmlFor="cartao_dia_facil" className="cursor-pointer">Saldo Livre</Label>
            </div>
            <div className="flex items-center space-x-2 gap-2">
              <RadioGroupItem value="outros" id="outros_cartao" />
              <Label htmlFor="outros_cartao" className="cursor-pointer">Outros:</Label>
              <Input
                type="text"
                value={data.tipo_cartao_outros}
                onChange={(e) => handleTextChange('tipo_cartao_outros', e.target.value)}
                placeholder="Especifique"
                className="flex-1"
                disabled={data.tipo_cartao !== 'outros'}
              />
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Total Diário */}
      <Card className={excedeValor ? "border-yellow-500" : ""}>
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <p className="text-lg">
              Utilizando diariamente{' '}
              <span className="text-2xl font-bold text-primary">
                R$ {totalDiario.toFixed(2)}
              </span>
              {' '}para locomoção entre residência e trabalho.
            </p>
            
            {excedeValor && (
              <Alert variant="default" className="bg-yellow-50 border-yellow-500">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  O valor diário excede R$ 21,00. Será necessária confirmação adicional ao salvar.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
